import { useEffect, useState } from "react";
import api, { formatAxiosError } from "@/lib/api";
import { useDialog } from "@/context/DialogContext";
import { Briefcase, Plus, PencilSimple, Trash, X, FloppyDisk, Lock } from "@phosphor-icons/react";

const EMPTY = { name: "", department: "", description: "", requirements: "", responsibilities: "", skills: [] };

export default function JobTemplatesTab() {
  const { confirm } = useDialog();
  const [tpls, setTpls] = useState([]);
  const [dlg, setDlg] = useState(null);
  const [msg, setMsg] = useState({ type: "", text: "" });

  const flash = (type, text) => { setMsg({ type, text }); setTimeout(() => setMsg({ type: "", text: "" }), 4000); };
  const load = async () => {
    try { const r = await api.get("/job-templates"); setTpls(r.data.templates || []); }
    catch (e) { flash("error", formatAxiosError(e)); }
  };
  useEffect(() => { load(); }, []);

  const del = async (t) => {
    const ok = await confirm({
      title: `Delete template "${t.name}"?`,
      description: "The template will no longer be available when creating new jobs.",
      confirmText: "Delete",
      variant: "destructive",
    });
    if (!ok) return;
    try { await api.delete(`/job-templates/${t.id}`); await load(); flash("ok", "Deleted."); }
    catch (e) { flash("error", formatAxiosError(e)); }
  };

  return (
    <div className="max-w-5xl">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="flex items-center gap-2 mb-1"><Briefcase size={16} weight="bold" className="text-[#002FA7]" />
            <h3 className="font-display text-xl font-black">Job templates</h3></div>
          <p className="text-sm text-muted-foreground">Reusable job description templates. Users pick a template when creating a job opening.</p>
        </div>
        <button onClick={() => setDlg({ mode: "create", form: EMPTY })} className="btn-primary flex items-center gap-2" data-testid="new-tpl-btn">
          <Plus size={14} weight="bold" /> New template
        </button>
      </div>
      {msg.text && <div className={`mb-4 text-xs px-3 py-2 border ${msg.type === "ok" ? "border-green-500 bg-green-50 text-green-700" : "border-red-500 bg-red-50 text-red-700"}`}>{msg.text}</div>}

      <div className="border border-border">
        <table className="w-full text-sm">
          <thead className="bg-muted border-b border-border">
            <tr>
              <th className="text-left px-3 py-2 font-semibold">Name</th>
              <th className="text-left px-3 py-2 font-semibold">Department</th>
              <th className="text-left px-3 py-2 font-semibold">Skills</th>
              <th className="text-left px-3 py-2 font-semibold">Actions</th>
            </tr>
          </thead>
          <tbody>
            {tpls.map(t => (
              <tr key={t.id} className="border-b border-border">
                <td className="px-3 py-2 font-semibold">
                  {t.name}{t.is_default && <span className="ml-2 text-[10px] text-[#002FA7] inline-flex items-center gap-0.5"><Lock size={10} weight="bold" /> DEFAULT</span>}
                </td>
                <td className="px-3 py-2">{t.department || "—"}</td>
                <td className="px-3 py-2 text-xs">{(t.skills || []).slice(0, 4).join(", ")}{(t.skills || []).length > 4 && "…"}</td>
                <td className="px-3 py-2 flex gap-1">
                  <button onClick={() => setDlg({ mode: "edit", tpl: t, form: { ...EMPTY, ...t } })} className="p-1 hover:bg-muted" title="Edit"><PencilSimple size={14} weight="bold" /></button>
                  {!t.is_default && <button onClick={() => del(t)} className="p-1 hover:bg-muted text-red-600" title="Delete"><Trash size={14} weight="bold" /></button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {dlg && <TplDialog dlg={dlg} onClose={() => setDlg(null)} onSaved={async () => { setDlg(null); await load(); flash("ok", "Saved."); }} />}
    </div>
  );
}

function TplDialog({ dlg, onClose, onSaved }) {
  const [form, setForm] = useState(dlg.form);
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);
  const [skillInput, setSkillInput] = useState("");

  const addSkill = () => {
    const s = skillInput.trim();
    if (!s) return;
    if (!form.skills.includes(s)) setForm({ ...form, skills: [...form.skills, s] });
    setSkillInput("");
  };

  const save = async () => {
    setErr(""); setSaving(true);
    try {
      if (dlg.mode === "create") await api.post("/job-templates", form);
      else await api.patch(`/job-templates/${dlg.tpl.id}`, form);
      await onSaved();
    } catch (e) { setErr(formatAxiosError(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white border border-border max-w-3xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-border flex items-center justify-between">
          <h3 className="font-display text-xl font-black">{dlg.mode === "create" ? "New job template" : `Edit "${dlg.tpl.name}"`}</h3>
          <button onClick={onClose} className="p-1 hover:bg-muted"><X size={16} weight="bold" /></button>
        </div>
        <div className="p-6 space-y-3">
          {err && <div className="text-xs text-red-700 border border-red-500 bg-red-50 px-3 py-2">{err}</div>}
          <div className="grid grid-cols-2 gap-3">
            <F label="Name*"><input className="input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></F>
            <F label="Department"><input className="input" value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })} /></F>
          </div>
          <F label="Description"><textarea className="input min-h-[100px]" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></F>
          <F label="Requirements"><textarea className="input min-h-[120px] font-mono text-xs" value={form.requirements} onChange={(e) => setForm({ ...form, requirements: e.target.value })} /></F>
          <F label="Responsibilities"><textarea className="input min-h-[120px] font-mono text-xs" value={form.responsibilities} onChange={(e) => setForm({ ...form, responsibilities: e.target.value })} /></F>
          <F label="Skills">
            <div className="flex flex-wrap gap-1 mb-2">
              {form.skills.map((s, i) => (
                <span key={i} className="inline-flex items-center gap-1 text-xs px-2 py-0.5 bg-muted border border-border">
                  {s}<button onClick={() => setForm({ ...form, skills: form.skills.filter((_, j) => j !== i) })} className="text-red-600">×</button>
                </span>
              ))}
            </div>
            <div className="flex gap-2">
              <input className="input flex-1" value={skillInput} onChange={(e) => setSkillInput(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addSkill(); }}} placeholder="Add a skill and press Enter" />
              <button onClick={addSkill} className="px-3 py-2 border border-border hover:bg-muted text-sm">Add</button>
            </div>
          </F>
        </div>
        <div className="px-6 py-4 border-t border-border flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm border border-border hover:bg-muted">Cancel</button>
          <button onClick={save} disabled={saving || !form.name.trim()} className="btn-primary flex items-center gap-2">
            <FloppyDisk size={14} weight="bold" /> {saving ? "Saving…" : "Save template"}
          </button>
        </div>
      </div>
    </div>
  );
}
function F({ label, children }) { return (<label className="block"><span className="block text-xs font-mono-mini uppercase tracking-widest text-muted-foreground mb-1">{label}</span>{children}</label>); }
